import React, { useEffect, useState } from "react";
import { ConnectedProps, connect } from "react-redux";
import { ReducerState, WifiConnectArgs } from "../../types";
import { useGetDeviceByIdQuery } from "../../actions/devicesInQuery";
import { Modal, Button, Form, Typography, message } from "antd";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHook";
import { setWifiModalOpen } from "../../reducers/devicesSlice";
import { ReactComponent as BluetoothIcon } from "../../assets/icons/bluetooth.svg";
import DeviceSetupForm from "./DeviceSetupForm";
import WifiSuccessMessage from "./WifiSuccessMessage";
import { BLUETOOTH_STATES } from "./deviceProfiles";
import { useBluetooth } from "./useBluetooth";
import { useDeviceWifiUpdateMutation } from "../../actions/devicesInQuery";
import { displayErrorNotification } from "../../actions";
import "./index.css";

const { Text } = Typography;

const mapStateToProps = (state: ReducerState) => {
  return {
    selected: state.devices?.selected?.uuid || null,
  };
};

const connector = connect(mapStateToProps, { displayErrorNotification });

type Props = ConnectedProps<typeof connector> & {};

const Index: React.FC<Props> = ({ selected, displayErrorNotification }) => {
  const handleSendErrorMessage = (msg: string) => {
    displayErrorNotification(msg);
  };

  const {
    connect,
    disconnect,
    connected,
    bluetoothState,
    scanWifi,
    startWifiConnect,
    isWifiConnected,
    currentNetwork,
  } = useBluetooth({ handleSendErrorMessage });

  const dispatch = useAppDispatch();
  const { data: selectedDevice } = useGetDeviceByIdQuery(selected!, {
    skip: !selected,
  });
  const isModalOpen = useAppSelector((state) => state.devices.isWifiModalOpen);
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm<WifiConnectArgs>();
  const ssid = Form.useWatch("ssid", form);
  const [targetWifi, setTagetWifi] = useState<string>("");
  const [deviceWifiUpdate] = useDeviceWifiUpdateMutation();
  const [wifiList, setWifiList] = useState<string[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);

  const onFinishFormSubmit = async (values: WifiConnectArgs) => {
    setTagetWifi(values.ssid);
    startWifiConnect(values.ssid, values.psk);
  };

  useEffect(() => {
    if (targetWifi.length > 0 && targetWifi === currentNetwork) {
      //connect to wifi success
      messageApi.open({
        content: (
          <WifiSuccessMessage
            title="Device connected:"
            description={selectedDevice?.display_name || ""}
            onClose={() => messageApi.destroy()}
          />
        ),
        duration: 0,
        className: "success-wifi-connect-holder",
      });
      deviceWifiUpdate({
        deviceUuid: selectedDevice?.uuid || "",
        wifi: targetWifi,
      });
    }
  }, [
    targetWifi,
    messageApi,
    selectedDevice,
    currentNetwork,
    deviceWifiUpdate,
  ]);

  useEffect(() => {
    if (
      bluetoothState === BLUETOOTH_STATES.CONNECTED &&
      wifiList.length === 0 &&
      !isConnecting
    ) {
      setIsConnecting(true);
      setTimeout(async () => {
        const ssids = await scanWifi();
        setWifiList(ssids);
        setIsConnecting(false);
      }, 1000);
    }
  }, [
    bluetoothState,
    wifiList,
    setWifiList,
    scanWifi,
    isConnecting,
    setIsConnecting,
  ]);

  // actions
  const onScanButtonPressed = async () => {
    const success = await connect();
    if (!success) {
      displayErrorNotification(
        `Failed to connect to ${selectedDevice}. Please refresh the page and try again.`,
      );
    }
  };

  const onCancelButtonPressed = async () => {
    disconnect();
    form.resetFields();
    messageApi.destroy();
  };

  const onRescanButtonPressed = async () => {
    const ssids = await scanWifi();
    setWifiList(ssids);
  };

  return (
    <Modal
      open={isModalOpen}
      onCancel={() => {
        onCancelButtonPressed();
        dispatch(setWifiModalOpen(false));
      }}
      onOk={() => {
        form.submit();
        // dispatch(setWifiModalOpen(false));
      }}
      styles={{
        content: {
          borderRadius: ".5rem",
          border: "1px solid #0C4A6E",
        },
      }}
      closeIcon={null}
      okText={
        selectedDevice?.wifi && selectedDevice?.wifi?.length > 0
          ? "Change Wifi"
          : "Connect"
      }
      cancelButtonProps={{
        className: "btn-wifi-cancel",
      }}
      okButtonProps={{
        className: "btn-wifi-confirm",
        disabled: !(ssid?.length > 0),
      }}
    >
      {contextHolder}
      <div>
        <div className="mb-4">
          <Text className="text-[24px] font-medium ">
            Device:
            {selectedDevice?.display_name ? selectedDevice?.display_name : ""}
          </Text>
        </div>
        {selectedDevice?.wifi && selectedDevice?.wifi?.length > 0 && (
          <div className="mb-2">
            <Text className="text-[16px]">
              Connected Wifi: {selectedDevice?.wifi}
            </Text>
          </div>
        )}
        {bluetoothState === BLUETOOTH_STATES.UNSUPPORTED ? (
          <h3>
            This browser does not support WebBluetooth. Please use a supported
            browser such as Google Chrome for this feature.
          </h3>
        ) : (
          <div className="w-full flex items-center justify-center">
            {bluetoothState === BLUETOOTH_STATES.DISCONNECTED && (
              <Button
                onClick={onScanButtonPressed}
                type="primary"
                shape="round"
                about="Scan"
                className="device-setup-scan-button"
                icon={<BluetoothIcon className="bluetooth-icon" fill="white" />}
                block
              />
            )}
            <DeviceSetupForm
              form={form}
              bluetoothState={bluetoothState}
              isWifiConnected={isWifiConnected || !!currentNetwork}
              handleFinish={onFinishFormSubmit}
              handleRescan={onRescanButtonPressed}
              wifiList={wifiList}
              currentNetwork={currentNetwork}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default connector(Index);
